import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  firstValueFrom,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AccountApi } from 'src/app/shared/apis/account.api';
import { ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { AccountInforData, SocialData, TSocialUserType } from 'src/app/shared/interfaces/account.interface';
import { FaValidatorType } from 'src/app/shared/interfaces/otp-method.interface';

type TThirdPartyInfoTable = {
  [key in TSocialUserType]?: SocialData;
};
@Injectable({ providedIn: 'root' })
export class SecurityService {
  public constructor(
    private accApi: AccountApi,
    private appService: AppService,
  ) {
    this.updateUserInfo$.subscribe(async () => {
      const userInfo = await firstValueFrom(this.accApi.getUserAccountInfo());
      this.appService.userInfo$.next(userInfo.data);
    });
  }
  public currentOtpMethod: FaValidatorType = 'None';
  public otpMethods: ICSOption[] = [];

  public isBindingEmail2fa: boolean = false;

  public isBindingMobile2fa: boolean = false;

  public isBindingGoogle2fa: boolean = false;

  public isBindingPassword: boolean = false;

  public updateUserInfo$: Subject<void> = new Subject<void>();

  public uerInfo: AccountInforData | null = null;

  public otpMethod$ = this.appService.userInfo$.subscribe(userInfo => {
    if (!userInfo) {
      return;
    }
    const defaultOtpMethod: string = userInfo.userSetting.defaultOtpMethod;
    const supportOtpMethod: string[] = userInfo.userSetting.supportOtpMethod;
    this.uerInfo = userInfo;

    this.currentOtpMethod = defaultOtpMethod as FaValidatorType;
    this.otpMethods = supportOtpMethod.map(method => ({
      value: method,
      label: method === 'Google' ? 'Authenticator' : method,
    }));
    this.isBindingEmail2fa = userInfo.userSetting.supportOtpMethod.includes('Email');
    this.isBindingMobile2fa = userInfo.userSetting.supportOtpMethod.includes('Mobile');
    this.isBindingGoogle2fa = userInfo.userSetting.supportOtpMethod.includes('Google');

    this.isBindingPassword = userInfo.hasPassword;
  });

  public updateThirdPartyMethod$: Subject<void> = new Subject<void>();

  public thirdPartyInfoTable: TThirdPartyInfoTable = {};

  public userSocialInfoList: SocialData[] = [];

  /** A) 以 BehaviorSubject 內部保存最新列表（可選） */
  private _userSocialInfoList = new BehaviorSubject<SocialData[]>([]);
  /** 暴露唯讀 Observable 給外部訂閱 */
  readonly userSocialInfoList$ = this._userSocialInfoList.asObservable();

  public thirdPartyMethod$ = this.updateThirdPartyMethod$
    .pipe(
      startWith(void 0), // 初次載入
      switchMap(() =>
        // 等到 userInfo$ 發出非 null（表示已登入）才往下
        this.appService.userInfo$.pipe(
          filter((u): u is AccountInforData => !!u),
          take(1), // 每次觸發只取一次當前登入狀態
          switchMap(() =>
            this.accApi.getSocialList().pipe(
              tap(e => {
                if (!e) return;
                this.thirdPartyInfoTable = e.socialInfoList.reduce<TThirdPartyInfoTable>((acc, cur) => {
                  acc[cur.socialUserType] = cur;
                  return acc;
                }, {});
                this.userSocialInfoList = e.socialInfoList;
                this._userSocialInfoList.next(e.socialInfoList);
              }),
              catchError(err => {
                console.error(err);
                return of(null);
              }),
            ),
          ),
        ),
      ),
    )
    .subscribe();
}
